import { useCallback, useEffect, useState } from 'react';

import { type TypeSelectOption, toastService } from '@thefarmersfront/kitchen';

import { formatDate, formatNumber } from '~/utils';

import { DEFAULT_INPUT_STATUS, RECEIVE, TOAST_MESSAGE } from '~/constants';

import { fetchInboundAreaOperationDates, fetchInboundAreaOperationTimes } from './queries';

import type { TypeErrorResponseData, TypeInputState } from '~/types/common.types';

const ERROR_MESSAGE = {
  NOT_AVAILABLE_DATE: '요청 불가능한 날짜입니다.',
  NOT_AVAILABLE_TIME: '요청 불가능한 시간입니다. 다시 선택해 주세요.',
  REQUIRED_FIELD: '필수 항목입니다.',
};

type UseOperationDateTimeParams = {
  /** 주문 ID */
  orderId: string | number;
  /** 입고 방법 */
  releaseCode: TypeSelectOption<string> | null;
  /** 입고예정일 */
  expectedDate: Date | null;
  /** 입고예정시간 (등록: TypeSelectOption, 상세: string) */
  expectedTime: TypeSelectOption<string> | string | null;
  /** 택배 여부 */
  isParcel: boolean;
  /** 데이터 업데이트 콜백 */
  onUpdate: (updates: { expectedDate?: Date | null; expectedTime?: TypeSelectOption<string> | string | null }) => void;
  /** Validation 상태 업데이트 콜백 */
  onValidationChange: (state: { expectedDate: TypeInputState; expectedTime: TypeInputState }) => void;
  /** 상세 모달 모드 여부 (초기 운영시간 텍스트 표시 여부 결정) */
  isDetailMode?: boolean;
  /** 상세 모달의 초기 운영시간 ID (수정 전 선택된 시간) */
  initialOperationTimeId?: number;
};

type UseOperationDateTimeReturn = {
  /** 입고 가능한 날짜 목록 */
  operationDates: string[];
  /** 입고 가능한 시간 옵션 목록 */
  operationTimes: TypeSelectOption<string>[];
  /** 마지막 운영시간 조회 시각 */
  lastFetchedAt: Date | null;
  isLoading: boolean;
  /** 날짜 변경 핸들러 */
  handleDateChange: (date: Date | null) => Promise<void>;
  /** 시간 변경 핸들러 */
  handleTimeChange: (time: TypeSelectOption<string>) => void;
  /** 운영시간 새로고침 */
  refresh: () => Promise<void>;
  /** Validation 실행 */
  validate: () => boolean;
};

/**
 * [입고관리] 입고요청 등록/상세 - 입고예정일시 관리 Custom Hook
 *
 * 날짜/시간 선택, API 호출, validation을 통합 관리합니다.
 *
 * @example
 * ```tsx
 * const { operationDates, operationTimes, handleDateChange, validate } = useOperationDateTime({
 *   orderId: order.id,
 *   releaseCode: filter.releaseCode,
 *   expectedDate: filter.expectedDate,
 *   expectedTime: filter.expectedTime,
 *   isParcel: filter.releaseCode?.value === 'PARCEL',
 *   onUpdate: (updates) => setFilter({ ...filter, ...updates }),
 *   onValidationChange: (state) => setValidation(state),
 * });
 * ```
 */
export const useOperationDateTime = ({
  orderId,
  releaseCode,
  expectedDate,
  expectedTime,
  isParcel,
  onUpdate,
  onValidationChange,
  isDetailMode = false,
  initialOperationTimeId,
}: UseOperationDateTimeParams): UseOperationDateTimeReturn => {
  const [operationDates, setOperationDates] = useState<string[]>([]);
  const [operationTimes, setOperationTimes] = useState<TypeSelectOption<string>[]>([]);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOperationDates = useCallback(async (): Promise<string[]> => {
    if (!releaseCode) return [];

    try {
      const params = {
        orderId: typeof orderId === 'string' ? Number(orderId) : orderId,
        releaseCode: releaseCode.value,
      };

      const { dates } = await fetchInboundAreaOperationDates(params);
      setOperationDates(dates);

      return dates;
    } catch (error) {
      if ((error as TypeErrorResponseData['error'])?.code === RECEIVE.RECEIVE_DATE_TIME_NOT_EXIST) {
        setOperationDates([]);
      }

      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });

      return [];
    }
  }, [orderId, releaseCode]);

  const fetchOperationTimes = useCallback(
    async (date: Date | null = expectedDate): Promise<TypeSelectOption<string>[]> => {
      if (!releaseCode || !date) return [];

      try {
        setIsLoading(true);

        const params = {
          orderId: typeof orderId === 'string' ? Number(orderId) : orderId,
          releaseCode: releaseCode.value,
          receivingExpectedDate: formatDate(date, 'YYYY-MM-DD') ?? '',
        };

        const { operationTimes } = await fetchInboundAreaOperationTimes(params);

        const formattedOperationTimes = operationTimes.map((time) => {
          const isInitOperationTime = isDetailMode && initialOperationTimeId === time.operationTimeId;

          return {
            value: String(time.operationTimeId),
            text: isInitOperationTime
              ? time.operationTime
              : `${time.operationTime} | 입고가능 : ${formatNumber(time.availableSku)}종 ${formatNumber(
                  time.availableUnit
                )}개`,
            disabled: !isInitOperationTime && time.disabled === true,
          };
        });

        setOperationTimes(formattedOperationTimes);
        setLastFetchedAt(new Date());

        return formattedOperationTimes;
      } catch (error) {
        if ((error as TypeErrorResponseData['error'])?.code === RECEIVE.RECEIVE_DATE_TIME_NOT_EXIST) {
          return [];
        }

        toastService.addToast({
          type: 'error',
          message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
        });

        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [orderId, releaseCode, expectedDate, isDetailMode, initialOperationTimeId]
  );

  /**
   * 날짜 변경 핸들러
   */
  const handleDateChange = useCallback(
    async (date: Date | null) => {
      if (date === expectedDate) return;

      // 1. Validation 상태 리셋
      onValidationChange({
        expectedDate: DEFAULT_INPUT_STATUS,
        expectedTime: DEFAULT_INPUT_STATUS,
      });

      // 2. 날짜가 없으면 초기화 후 종료
      if (date === null) {
        setLastFetchedAt(null);
        onUpdate({ expectedDate: date, expectedTime: null });
        return;
      }

      // 3. 시간 옵션 조회
      const times = await fetchOperationTimes(date);

      // 4. 날짜 변경 및 시간 설정
      const updates: { expectedDate: Date | null; expectedTime: TypeSelectOption<string> | string | null } = {
        expectedDate: date,
        expectedTime: null,
      };

      // 5. 택배인 경우 첫 번째 시간 자동 선택
      if (isParcel && times.length > 0) {
        updates.expectedTime = times[0];
      }

      onUpdate(updates);
    },
    [expectedDate, isParcel, onUpdate, onValidationChange, fetchOperationTimes]
  );

  /**
   * 시간 변경 핸들러
   */
  const handleTimeChange = useCallback(
    (time: TypeSelectOption<string>) => {
      onUpdate({ expectedTime: time });
      onValidationChange({ expectedDate: DEFAULT_INPUT_STATUS, expectedTime: DEFAULT_INPUT_STATUS });
    },
    [onUpdate, onValidationChange]
  );

  /**
   * 운영시간 새로고침
   * - 날짜/시간 재조회
   * - 현재 선택값 검증
   */
  const refresh = useCallback(async () => {
    const dates = await fetchOperationDates();

    let needsUpdate = false;
    const updates: { expectedDate?: Date | null; expectedTime?: TypeSelectOption<string> | string | null } = {};
    const validationUpdates: { expectedDate: TypeInputState; expectedTime: TypeInputState } = {
      expectedDate: DEFAULT_INPUT_STATUS,
      expectedTime: DEFAULT_INPUT_STATUS,
    };

    // 현재 선택된 날짜가 유효한지 검증
    const isCurrentDateDisabled = expectedDate && !dates.includes(formatDate(expectedDate, 'YYYY-MM-DD') ?? '');

    if (isCurrentDateDisabled) {
      updates.expectedDate = null;
      updates.expectedTime = null;
      validationUpdates.expectedDate = { status: 'error', message: ERROR_MESSAGE.NOT_AVAILABLE_DATE };
      setLastFetchedAt(null);
      needsUpdate = true;
    }

    // 시간 옵션 재조회
    const times = await fetchOperationTimes(updates.expectedDate === null ? null : expectedDate);

    // 택배인 경우: 첫 번째 시간 자동 선택
    if (isParcel && times.length > 0) {
      updates.expectedTime = times[0];
      needsUpdate = true;
    } else if (!isParcel) {
      // 택배가 아닌 경우: 현재 선택된 시간이 유효한지 검증
      const currentTimeValue = typeof expectedTime === 'string' ? expectedTime : expectedTime?.value;
      const selectedTime = times.find((t) => t.value === currentTimeValue);

      if (currentTimeValue && !selectedTime) {
        // 현재 시간이 삭제된 경우
        updates.expectedTime = null;
        const hasOtherSelectableTime = times.some((t) => !t.disabled);
        if (hasOtherSelectableTime) {
          validationUpdates.expectedTime = {
            status: 'error',
            message: ERROR_MESSAGE.NOT_AVAILABLE_TIME,
          };
        }
        needsUpdate = true;
      } else if (selectedTime?.disabled) {
        // 현재 시간이 비활성화된 경우
        validationUpdates.expectedTime = {
          status: 'error',
          message: ERROR_MESSAGE.NOT_AVAILABLE_TIME,
        };
      }
    }

    if (needsUpdate) {
      onUpdate(updates);
    }

    onValidationChange(validationUpdates);
  }, [fetchOperationDates, fetchOperationTimes, expectedDate, expectedTime, isParcel, onUpdate, onValidationChange]);

  /**
   * Validation 실행
   * @returns 유효성 검사 통과 여부
   */
  const validate = useCallback((): boolean => {
    const errors: { expectedDate: TypeInputState; expectedTime: TypeInputState } = {
      expectedDate: DEFAULT_INPUT_STATUS,
      expectedTime: DEFAULT_INPUT_STATUS,
    };

    // 날짜 필수 검증
    if (!expectedDate) {
      errors.expectedDate = { status: 'error', message: ERROR_MESSAGE.REQUIRED_FIELD };
    } else {
      // 날짜가 유효한 범위인지 검증
      const formattedDate = formatDate(expectedDate, 'YYYY-MM-DD');
      if (formattedDate && !operationDates.includes(formattedDate)) {
        errors.expectedDate = {
          status: 'error',
          message: ERROR_MESSAGE.NOT_AVAILABLE_DATE,
        };
      }
    }

    // 시간 필수 검증 (택배가 아닌 경우만)
    if (!isParcel) {
      const currentTimeValue = typeof expectedTime === 'string' ? expectedTime : expectedTime?.value;

      if (!currentTimeValue) {
        errors.expectedTime = { status: 'error', message: ERROR_MESSAGE.REQUIRED_FIELD };
      } else {
        // 시간이 유효한지 검증
        const selectedTime = operationTimes.find((t) => t.value === currentTimeValue);
        if (selectedTime?.disabled) {
          errors.expectedTime = {
            status: 'error',
            message: ERROR_MESSAGE.NOT_AVAILABLE_TIME,
          };
        }
      }
    }

    onValidationChange(errors);

    return Object.values(errors).every((error) => error.status !== 'error');
  }, [expectedDate, expectedTime, isParcel, operationDates, operationTimes, onValidationChange]);

  // 입고방법 변경 시 날짜 목록 조회
  useEffect(() => {
    if (releaseCode) {
      fetchOperationDates();
    }
  }, [releaseCode]);

  // 초기 데이터 로드 시 운영시간 조회 (상세 모달 전용)
  // - 상세 모달: 초기에 이미 날짜가 설정되어 있으므로 times fetch 필요
  // - 등록 모달: 날짜가 null이므로 실행 안 됨
  useEffect(() => {
    if (releaseCode && expectedDate && operationTimes.length === 0) {
      fetchOperationTimes(expectedDate);
    }
  }, [releaseCode, expectedDate]);

  return {
    operationDates,
    operationTimes,
    lastFetchedAt,
    isLoading,
    handleDateChange,
    handleTimeChange,
    refresh,
    validate,
  };
};

export default useOperationDateTime;
